import React, { useState } from 'react';
import { Dropdown, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { InvoiceProtocol, InvoiceProtocols } from '../models/table-models';
import { getBranchProtocols } from '../utils/clients-hooks';
import { transformDateToString } from '../utils/helper-functions';

/**
 * Протоколите на един ред: тези от периода на задължението с отметки, а под „+" - всички протоколи на
 * обекта.
 *
 * Списъкът под „+" се тегли чак при отваряне и само за този обект. Редовете носят от сървъра само
 * прозореца на своята фактура (от 1-о число на месеца до крайната дата), а точно оттук операторът търси
 * протокола, който прозорецът не е хванал - от по-стар месец или след крайната дата. Наведнъж за всички
 * редове това би било по една заявка на ред, а обединен документ носи и по двеста.
 *
 * Добавеният от списъка минава на реда като отметнат протокол и излиза от списъка.
 */
export const ProtocolPicker = ({
    row,
    loading,
    selectedProtocolIds,
    onToggleProtocol,
    added,
    onAddProtocol,
}: {
    row?: InvoiceProtocols;
    /** Протоколите за редовете още се теглят - клетката не бива да казва „няма", преди да знае. */
    loading: boolean;
    selectedProtocolIds: Set<number>;
    onToggleProtocol: (id: number) => void;
    /** Добавените на ръка за този ред. */
    added: InvoiceProtocol[];
    onAddProtocol: (protocol: InvoiceProtocol) => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });

    // Протоколите на обекта: празно, докато „+" не се отвори за първи път.
    const [branchProtocols, setBranchProtocols] = useState<InvoiceProtocol[]>([]);
    const [branchLoading, setBranchLoading] = useState(false);
    const [branchLoaded, setBranchLoaded] = useState(false);

    if (loading) return <Spinner animation='border' size='sm' className='text-muted' />;
    if (!row) return <span className='small text-muted'>{t('noProtocolsForPeriod')}</span>;

    const label = (protocol: InvoiceProtocol) =>
        `${transformDateToString(protocol.date, 'dd.mm.yyyy')}${
            protocol.taskCategory ? ' - ' + t(protocol.taskCategory) : ''
        }`;

    const shown = [...row.protocols, ...added];
    const shownIds = new Set(shown.map(({ id }) => id));
    // Докато обектът не е зареден, се предлага прозорецът - той вече е тук и не иска чакане.
    const offered = (branchLoaded ? branchProtocols : row.extraProtocols)
        .filter((protocol) => !shownIds.has(protocol.id))
        .filter((protocol, index, all) => all.findIndex((other) => other.id === protocol.id) === index);

    const loadBranchProtocols = async () => {
        if (branchLoaded || branchLoading) return;

        setBranchLoading(true);
        try {
            setBranchProtocols(await getBranchProtocols(row.branchId));
            setBranchLoaded(true);
        } catch {
            // Списъкът е удобство: остава прозорецът на фактурата, вместо диалогът да се счупи.
            setBranchLoaded(true);
        } finally {
            setBranchLoading(false);
        }
    };

    return (
        <>
            {shown.length > 0 ? (
                shown.map((protocol) => (
                    <Form.Check
                        key={protocol.id}
                        type='checkbox'
                        id={`protocol-${protocol.id}`}
                        className='small'
                        checked={selectedProtocolIds.has(protocol.id)}
                        onChange={() => onToggleProtocol(protocol.id)}
                        label={label(protocol)}
                    />
                ))
            ) : (
                <span className='small text-muted'>{t('noProtocolsForPeriod')}</span>
            )}
            <Dropdown className='mt-1' onToggle={(open) => open && loadBranchProtocols()}>
                <Dropdown.Toggle variant='outline-secondary' size='sm' className='py-0 px-2'>
                    + {t('addProtocol')}
                </Dropdown.Toggle>
                {/* Менюто се закача за body - иначе редът на таблицата го отрязва. */}
                <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount style={{ maxHeight: '18rem', overflowY: 'auto' }}>
                    {branchLoading && (
                        <div className='px-3 py-1 small text-muted d-flex align-items-center'>
                            <Spinner animation='border' size='sm' className='me-2' />
                            {t('loadingProtocols')}
                        </div>
                    )}
                    {!branchLoading && offered.length === 0 && (
                        <div className='px-3 py-1 small text-muted'>{t('noMoreProtocols')}</div>
                    )}
                    {offered.map((protocol) => (
                        <Dropdown.Item key={protocol.id} className='small' onClick={() => onAddProtocol(protocol)}>
                            {label(protocol)}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </>
    );
};
